/**
 * StaticHotelSearchField
 *
 * - Renders static, metro-grouped list of properties.
 * - Filters by property name/marsha/keyword and metro name.
 * - Guarantees: NO DUPLICATES in merged suggestions (while typing).
 * - Enforces: items with NO authored marshaCode are completely ignored (as if not in the model).
 */
import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import clsx from 'clsx';
import { InputTextField, DropDownModal } from '@marriott/mi-ui-library';
import { StyledSearchFieldCategoriesDiv } from '../SearchFieldCategories.styles';
import { StyledStaticHotelSearchField } from './StyledStaticHotelSearchField.styles';
import { useStaticDataContext } from '../../../modules/context';
import { useSearchFormStore } from '../../../modules/store/searchFormStore';
import { DESTINATION, DESTINATION_HIDDEN_FIELDS } from '../../../modules/store/store.constants';
import { eventUtil } from '../../../modules/utils';
import {
  normalizeGroups,
  buildSuggestionListNoDup,
  flattenProperties,
  hasValidMarsha,
  norm,
  keySuggestionNameOnly,
  keyGroup,
} from '../../../modules/utils';
import { StaticHotelSearchFieldProps, AuthoringProperty } from '../StaticHotelSearchField/StaticHotelSearchField.types';

export const StaticHotelSearchField: FC<StaticHotelSearchFieldProps> = ({
  metros,
  isDesktopView,
  isModalOpen,
  onCancel,
  changeMobileState,
  mobileModalHeading,
  mobilePlaceHolderText,
  hideClearCta = false,
  isMobileForm,
  showMarshaCode = false,
  highlightMatches = false,
  hotelIndentPx = 32,
  headerTransform = 'none',
}) => {
  const {
    destinationModuleEyebrowText,
    clearDestinationLabel,
    placeholderTextDesktop,
    noResultFound,
    noHotelsAvailableForStaticDestination,
  } = useStaticDataContext();
  const setSearchFormState = useSearchFormStore(s => s.setSearchFormState);
  const destinationFromStore = useSearchFormStore(s => s.destination);
  const formTopPosition = useSearchFormStore(s => s?.searchFormTopPosition);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(isModalOpen ?? false);
  const [showClearButton, setShowClearButton] = useState<boolean>(Boolean(destinationFromStore?.displayText?.length));
  const [searchText, setSearchText] = useState<string>(destinationFromStore?.displayText ?? '');
  const isSelectingRef = useRef<boolean>(false);
  const lastElementRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null); // container for dropdown content
  const inputElementRef = useRef<HTMLInputElement>(null); // direct ref to mobile input

  /* Normalize groups (merge duplicate metros + dedupe properties) + DROP "no marsha" entries */
  const normalizedGroups = useMemo(() => normalizeGroups(metros ?? []), [metros]);

  /* Flat list for suggestions (from normalized groups; all items have marsha) */
  const allPropertiesFlat = useMemo(() => flattenProperties(normalizedGroups), [normalizedGroups]);

  /* Branch by current text (no debounce → no flicker) */
  const isTypingNow = searchText.trim().length > 0;

  /* Build merged suggestions with name-only dedupe across metros */
  const suggestionList = useMemo(
    () => (isTypingNow ? buildSuggestionListNoDup(allPropertiesFlat, normalizedGroups, searchText) : []),
    [isTypingNow, allPropertiesFlat, normalizedGroups, searchText]
  );

  /* Presence flags */
  const hasGroupedResults = normalizedGroups.some(g => (g.properties ?? []).length > 0);
  const hasSuggestionResults = suggestionList.length > 0;

  /* Derived display text to avoid first-paint blank flicker (desktop & mobile) */
  const displayTextNow = destinationFromStore?.displayText ?? searchText;

  /* Effects: sync with store */
  useEffect(() => {
    const storeDisplayText = destinationFromStore?.displayText ?? '';
    setSearchText(storeDisplayText);
    setShowClearButton(Boolean(storeDisplayText.length));
  }, [destinationFromStore?.displayText]);

  useEffect(() => {
    if (isModalOpen) {
      const storeDisplayText = destinationFromStore?.displayText ?? '';
      setIsDropdownOpen(true);
      setSearchText(storeDisplayText);
      setShowClearButton(Boolean(storeDisplayText.length));
    }
  }, [isModalOpen, destinationFromStore?.displayText]);

  // Mobile takeover focus (safe; no-op on desktop). Keeps parity with SearchFieldCategories approach.

  useEffect(() => {
    if (!isDesktopView && (isModalOpen || isDropdownOpen)) {
      const t = setTimeout(() => inputElementRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isModalOpen, isDropdownOpen, isDesktopView]);

  /* Close dropdown on outside click or ESC, notify parent */
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleOutside = (ev: Event) => {
      const target = (ev.target as Node) ?? null;
      const inDropdown = !!dropdownRef.current && !!target && dropdownRef.current.contains(target);
      const inRoot = !!rootRef.current && !!target && rootRef.current.contains(target);
      if (!inDropdown && !inRoot) {
        setIsDropdownOpen(false);
        onCancel?.();
      }
    };
    const handleEsc = (ev: globalThis.KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setIsDropdownOpen(false);
        onCancel?.();
      }
    };
    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isDropdownOpen, onCancel]);

  // Opens the dropdown/modal so the list is visible.
  const openDropdown = useCallback(() => setIsDropdownOpen(true), []);

  // Handles click on the field; opens dropdown unless we are committing a selection.
  const handleFieldClick = useCallback(() => {
    if (isSelectingRef.current) return;
    openDropdown();
  }, [openDropdown]);

  // Handles typing in the input; updates local state, writes to store, and keeps dropdown open.
  const handleInputChange = useCallback(
    (val: string) => {
      setSearchText(val);
      setShowClearButton(Boolean(val?.length));
      setSearchFormState(
        [DESTINATION, DESTINATION_HIDDEN_FIELDS, 'destinationFieldDetails'],
        {
          [DESTINATION]: {
            ...(destinationFromStore ?? {}),
            displayText: val ?? '',
            destinationAddressPlaceId: '',
            secondaryDescription: '',
          },
          [DESTINATION_HIDDEN_FIELDS]: {
            latitude: '',
            longitude: '',
            destinationAddressMainText: val ?? '',
            destinationAddressSecondaryText: '',
            city: '',
            country: '',
            searchRadius: null,
          },
          destinationFieldDetails: { recentlyViewedPopertyCode: '' },
        },
        true
      );
      if (!isDropdownOpen) setIsDropdownOpen(true); // keep SAME dropdown; no flicker
    },
    [destinationFromStore, isDropdownOpen, setSearchFormState]
  );

  // Commits a selected hotel to the store and triggers desktop/mobile follow-up behavior.
  const selectProperty = useCallback(
    (p: AuthoringProperty) => {
      // Guard: should never happen, but keep rule—ignore items with no marsha
      if (!hasValidMarsha(p)) return;
      isSelectingRef.current = true;
      setSearchFormState([DESTINATION, DESTINATION_HIDDEN_FIELDS, 'destinationFieldDetails'], {
        [DESTINATION]: {
          ...(destinationFromStore ?? {}),
          displayText: p.propertyName,
          destinationAddressPlaceId: '',
          secondaryDescription: p.marshaCode,
        },
        [DESTINATION_HIDDEN_FIELDS]: {
          latitude: '',
          longitude: '',
          destinationAddressMainText: p.propertyName,
          destinationAddressSecondaryText: p.marshaCode,
          city: '',
          country: '',
          searchRadius: null,
          destinationType: 'Hotel Name',
        },
        destinationFieldDetails: { recentlyViewedPopertyCode: p.marshaCode },
      });
      if (isDesktopView) {
        eventUtil.dispatch('showSecondaryForm', true);
      } else {
        changeMobileState?.(p.propertyName);
      }
      setIsDropdownOpen(false);
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 150);
    },
    [destinationFromStore, isDesktopView, changeMobileState, setSearchFormState]
  );

  // Clears the input and resets related store fields; keeps dropdown open to show grouped list.
  const clearInput = useCallback(() => {
    setSearchText('');
    setShowClearButton(false);
    setIsDropdownOpen(true);
    if (isDesktopView) {
      eventUtil.dispatch('showSecondaryForm', false);
    }
    setSearchFormState(
      [DESTINATION, DESTINATION_HIDDEN_FIELDS, 'destinationFieldDetails'],
      {
        [DESTINATION]: {
          ...(destinationFromStore ?? {}),
          displayText: '',
          destinationAddressPlaceId: '',
          secondaryDescription: '',
        },
        [DESTINATION_HIDDEN_FIELDS]: {
          latitude: '',
          longitude: '',
          destinationAddressMainText: '',
          destinationAddressSecondaryText: '',
          city: '',
          country: '',
          searchRadius: null,
        },
        destinationFieldDetails: { recentlyViewedPopertyCode: '' },
      },
      true
    );
  }, [destinationFromStore, isDesktopView, setSearchFormState]);

  // Handles Enter key on the input: picks first group item or first suggestion; opens dropdown if closed.
  const handleEnterKey = useCallback(() => {
    if (isDropdownOpen) {
      if (!isTypingNow) {
        const firstGroup = normalizedGroups.find(g => (g.properties ?? []).length > 0);
        const firstProperty = firstGroup?.properties?.[0];
        if (firstProperty) {
          selectProperty(firstProperty);
          return;
        }
      } else {
        const firstSuggestion = suggestionList[0];
        if (firstSuggestion) {
          selectProperty(firstSuggestion);
          return;
        }
      }
    }
    openDropdown();
  }, [isDropdownOpen, isTypingNow, normalizedGroups, suggestionList, selectProperty, openDropdown]);

  // Handles keyboard on list items: Enter selects the focused hotel.
  const handleItemKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLLIElement>, property: AuthoringProperty) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        selectProperty(property);
      }
    },
    [selectProperty]
  );

  // Renders hotel name with optional <mark> highlighting for the typed substring.
  const renderPropertyLabel = useCallback(
    (name: string) => {
      const q = norm(searchText);
      if (!highlightMatches || !q) return name;
      const i = norm(name).indexOf(q);
      if (i < 0) return name;
      return (
        <>
          {name.slice(0, i)}
          <mark>{name.slice(i, i + q.length)}</mark>
          {name.slice(i + q.length)}
        </>
      );
    },
    [searchText, highlightMatches]
  );

  // IMPORTANT: show input even when isDesktopView is false during SSR/first paint
  // as long as mobile takeover isn't open. This removes desktop refresh flicker.
  const showOuterInput = isDesktopView || (!isDesktopView && !isDropdownOpen) || !!isMobileForm;

  const headerClass = clsx('metro-header', headerTransform === 'uppercase' && 'metro-header--uppercase');
  const indentClass =
    hotelIndentPx === 16
      ? 'hotel-row--indent-16'
      : hotelIndentPx === 24
      ? 'hotel-row--indent-24'
      : 'hotel-row--indent-32';

  return (
    <StyledSearchFieldCategoriesDiv
      $isTabbedSearchForm={false}
      $cityPickListToggle={false}
      data-testid="search-field-static-hotels"
      onClick={handleFieldClick}
      ref={rootRef}
    >
      <StyledStaticHotelSearchField $isDesktopView={isDesktopView}>
        {/* Desktop (and specific mobile forms) outer input */}
        {showOuterInput && (
          <InputTextField
            togglePlaceholder={true}
            label={destinationModuleEyebrowText}
            iconForLabel="icon-location"
            placeHolderText={placeholderTextDesktop ?? mobilePlaceHolderText}
            inputValue={displayTextNow}
            getInputValue={handleInputChange}
            handleKeyDown={handleEnterKey}
            trailingIconProps={{}}
            variation={isMobileForm ? 'default' : 'line-type'}
            showIcon={showClearButton && !hideClearCta && !!displayTextNow?.length}
            iconOnClick={clearInput}
            iconClass="icon-cancel clear-input-icon custom_click_track"
            iconAriaLabel={clearDestinationLabel}
            withinModal={!isDesktopView}
            showUnderline={isMobileForm}
            inputTextFieldClassName={'text-truncate custom_click_track'}
          />
        )}

        {/* Wrap dropdown in a real DOM container so outside-click detection can use dropdownRef */}
        {isDropdownOpen && (
          <div ref={dropdownRef}>
            <DropDownModal
              role="listbox"
              dropDownChildrenClassName="dropdown-children-custom-position"
              isAutoScrollEnabled={true}
              dropDownSpecificFlag={false}
              parentElementTopValue={formTopPosition?.value}
              handleBlur={true}
              scrollDownModalCloseState={false}
              labelledBy="search-field-dropdown"
              applyDefaultHeight={true}
              mobileTakeOver={isDesktopView ? false : true}
              mobileModalHeading={mobileModalHeading}
              show={isDropdownOpen}
              className={clsx('dropdown-container')}
              childrenClassName={clsx('search-field-dropdown', 'custom-branded-scrollbar')}
              mobileHeaderEnabled={isDesktopView ? false : true}
              dropdownModalOpenState={isDropdownOpen}
              setDropdownModalOpenState={setIsDropdownOpen}
              dropdownModalOnCLoseFunc={() => {
                setTimeout(() => {
                  setIsDropdownOpen(false);
                  onCancel?.();
                }, 100);
              }}
              customLastElementRef={lastElementRef}
            >
              {/* Mobile takeover input */}
              {!isDesktopView && (
                <div className={clsx('mobile-input-field', isMobileForm ? 'destination-label-medium-font' : '')}>
                  <InputTextField
                    ref={inputElementRef}
                    togglePlaceholder={true}
                    label={destinationModuleEyebrowText + ' '}
                    iconForLabel="icon-location"
                    placeHolderText={displayTextNow ? '' : mobilePlaceHolderText}
                    inputValue={displayTextNow}
                    getInputValue={handleInputChange}
                    handleKeyDown={handleEnterKey}
                    trailingIconProps={{}}
                    variation={isMobileForm ? 'default' : 'line-type'}
                    showIcon={showClearButton && !hideClearCta && !!displayTextNow?.length}
                    iconOnClick={clearInput}
                    iconClass="icon-cancel clear-input-icon custom_click_track"
                    iconAriaLabel={clearDestinationLabel}
                    withinModal={true}
                    showUnderline={true}
                    inputTextFieldClassName={'text-truncate'}
                  />
                </div>
              )}

              {/* Listbox: suggestions while typing OR full grouped list when not typing */}
              <ul id="search-field-dropdown" role="listbox" className={clsx('static-list', 'list-reset')}>
                {isTypingNow ? (
                  hasSuggestionResults ? (
                    suggestionList.map(property => {
                      const key = keySuggestionNameOnly(property); // dedupe identity == key identity (by name only)
                      return (
                        <li
                          key={key}
                          className={clsx('hotel-row', 'metro-hotels', indentClass)}
                          role="option"
                          tabIndex={0}
                          onMouseDown={event => event.stopPropagation()}
                          onClick={event => {
                            event.stopPropagation();
                            selectProperty(property);
                          }}
                          onKeyDown={event => handleItemKeyDown(event, property)}
                          aria-selected={false}
                        >
                          <span className="hotel-label">{renderPropertyLabel(property.propertyName)}</span>
                          {showMarshaCode && <span className="hotel-code"> ({property.marshaCode})</span>}
                        </li>
                      );
                    })
                  ) : (
                    <li className={clsx('no-results')} role="option" aria-disabled="true" aria-selected={false}>
                      {noResultFound}
                    </li>
                  )
                ) : hasGroupedResults ? (
                  normalizedGroups.map((group, idxGroup) => (
                    <li
                      key={`metro:${norm(group.metroArea)}:${idxGroup}`}
                      className={clsx('metro-group', idxGroup === 0 && 'metro-group--first')}
                      role="presentation"
                    >
                      <div className={headerClass} role="heading" aria-level={3} aria-label={group.metroArea}>
                        {group.metroArea}
                      </div>

                      <ul className={clsx('metro-hotels', 'list-reset')} role="group" aria-label={group.metroArea}>
                        {(group.properties ?? []).map(property => {
                          const key = keyGroup(property, group.metroArea);
                          return (
                            <li
                              key={key}
                              className={clsx('hotel-row', indentClass)}
                              role="option"
                              tabIndex={0}
                              onMouseDown={event => event.stopPropagation()}
                              onClick={event => {
                                event.stopPropagation();
                                selectProperty(property);
                              }}
                              onKeyDown={event => handleItemKeyDown(event, property)}
                              aria-selected={false}
                            >
                              <span className="hotel-label">{renderPropertyLabel(property.propertyName)}</span>
                              {showMarshaCode && <span className="hotel-code"> ({property.marshaCode})</span>}
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  ))
                ) : (
                  <li className={clsx('no-results')} role="option" aria-disabled="true" aria-selected={false}>
                    {noHotelsAvailableForStaticDestination}
                  </li>
                )}
              </ul>
            </DropDownModal>
          </div>
        )}
      </StyledStaticHotelSearchField>
    </StyledSearchFieldCategoriesDiv>
  );
};
